// src/components/Sidebar.tsx
import React, { useState, useMemo, useCallback, memo } from 'react';
import { NavLink } from 'react-router-dom';
import { Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Toolbar, Divider, Typography, Menu, MenuItem, Badge } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import ChurchIcon from '@mui/icons-material/Church';
import LanguageIcon from '@mui/icons-material/Language';
import ChatIcon from '@mui/icons-material/Chat';
import FolderIcon from '@mui/icons-material/Folder';
import ArticleIcon from '@mui/icons-material/Article';
import BarChartIcon from '@mui/icons-material/BarChart';
import { useAuth } from '../hooks/useAuth';
import { useMessaging } from '../contexts/MessagingContext';
import { useTranslation } from 'react-i18next';
import { changeLanguage as changeAppLanguage } from '../i18n';

interface SidebarProps {
  drawerWidth: number;
  mobileOpen: boolean;
  handleDrawerToggle: () => void;
}

const languages = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'ti', name: 'ትግርኛ', flag: '🇪🇷' },
  { code: 'am', name: 'አማርኛ', flag: '🇪🇹' },
];

const Sidebar: React.FC<SidebarProps> = ({ drawerWidth, mobileOpen, handleDrawerToggle }) => {
  const { authState } = useAuth();
  const { state: messagingState } = useMessaging();
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Total unread messages (DM + groups)
  const totalUnread = messagingState.unreadCount + messagingState.totalGroupUnread;

  const handleLanguageClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleLanguageClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleChangeLanguage = useCallback((lng: string) => {
    changeAppLanguage(lng);
    setAnchorEl(null);
  }, []);

  const currentLanguage = useMemo(() =>
    languages.find(l => l.code === i18n.language) || languages[0],
    [i18n.language]
  );

  // Memoized menu items to prevent recreation on every render
  const menuItems = useMemo(() => {
    const items = [
      { text: t('sidebar.dashboard'), icon: <DashboardIcon />, path: '/dashboard' },
      {
        text: t('sidebar.messaging', 'Messages'),
        icon: (
          <Badge badgeContent={totalUnread} color="error" max={99}>
            <ChatIcon />
          </Badge>
        ),
        path: '/messaging'
      },
      { text: t('sidebar.files', 'Fichiers'), icon: <FolderIcon />, path: '/files' },
    ];

    // Church menu based on role
    if (authState.user?.role === 'SuperAdmin') {
      items.push({ text: t('sidebar.churches'), icon: <ChurchIcon />, path: '/churches' });
    } else if (authState.user?.role === 'Admin') {
      items.push({ text: t('sidebar.myChurch'), icon: <ChurchIcon />, path: '/admin' });
    }

    // Posts menu for Admin and SuperAdmin
    if (authState.user?.role === 'SuperAdmin' || authState.user?.role === 'Admin') {
      items.push({ text: t('sidebar.posts', 'Posts'), icon: <ArticleIcon />, path: '/posts' });
    }

    // Analytics for SuperAdmin
    if (authState.user?.role === 'SuperAdmin') {
      items.push({ text: t('sidebar.analytics', 'Analytics'), icon: <BarChartIcon />, path: '/analytics' });
    }

    // SuperAdmin page
    if (authState.user?.role === 'SuperAdmin') {
      items.push({ text: t('sidebar.superadmin'), icon: <SupervisedUserCircleIcon />, path: '/superadmin' });
    }

    // Settings always at the end
    items.push({ text: t('sidebar.settings'), icon: <SettingsIcon />, path: '/settings' });

    return items;
  }, [authState.user?.role, t, totalUnread]);

  const drawerContent = useMemo(() => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'white' }}>
      <Toolbar sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: '1px solid #e2e8f0' }}>
        <Typography variant="h5" sx={{ color: '#2563eb', fontWeight: 700, letterSpacing: '-0.5px' }}>ZEMEN</Typography>
      </Toolbar>
      <List sx={{ flexGrow: 1, py: 2 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ px: 1.5, mb: 0.5 }}>
            <NavLink
              to={item.path}
              style={{ textDecoration: 'none', color: 'inherit', display: 'block', width: '100%' }}
            >
              {({ isActive }) => (
                <ListItemButton
                  onClick={handleDrawerToggle}
                  sx={{
                    borderRadius: 1,
                    backgroundColor: isActive ? '#dbeafe' : 'transparent',
                    color: isActive ? '#2563eb' : '#64748b',
                    '&:hover': {
                      backgroundColor: isActive ? '#dbeafe' : '#f1f5f9',
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: isActive ? '#2563eb' : '#64748b', minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontWeight: isActive ? 600 : 500,
                      fontSize: '0.9rem',
                    }}
                  />
                </ListItemButton>
              )}
            </NavLink>
          </ListItem>
        ))}
      </List>
      <Divider />
      {/* Language selector */}
      <Box sx={{ p: 2 }}>
        <ListItemButton onClick={handleLanguageClick} sx={{ borderRadius: 1, bgcolor: '#f8fafc' }}>
          <ListItemIcon sx={{ minWidth: 40 }}><LanguageIcon sx={{ color: '#64748b' }} /></ListItemIcon>
          <ListItemText
            primary={currentLanguage.flag + ' ' + currentLanguage.name}
            secondary={t('sidebar.language')}
            primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
            secondaryTypographyProps={{ fontSize: '0.75rem' }}
          />
        </ListItemButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleLanguageClose}
          PaperProps={{ sx: { borderRadius: 1, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } }}
        >
          {languages.map((lang) => (
            <MenuItem
              key={lang.code}
              onClick={() => handleChangeLanguage(lang.code)}
              selected={i18n.language === lang.code}
              sx={{ fontSize: '0.875rem' }}
            >
              <span style={{ marginRight: 8 }}>{lang.flag}</span>
              {lang.name}
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </Box>
  ), [menuItems, handleDrawerToggle, handleLanguageClick, handleLanguageClose, handleChangeLanguage, currentLanguage, anchorEl, i18n.language, t]);

  return (
    <Box
      component="nav"
      sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      aria-label="navigation menu"
    >
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            borderRight: '1px solid #e2e8f0',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            borderRight: '1px solid #e2e8f0',
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
};

export default memo(Sidebar);
